import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { downloadGraphFileApi } from "@/api/api"
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Eye,
  GripVertical,
  Loader2,
  Play,
  RotateCcw,
  Square,
  TestTube2,
  X,
} from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { WorkflowTestPanelInputs } from "./workflow-test-panel-inputs"
import { useTestRunUserInputs } from "../hooks/use-test-run-user-inputs"
import { useWorkflowTestRun } from "../hooks/use-workflow-test-run"
import { useWorkflowStore } from "../stores/workflow-store"
import { resolveStartFields, type StartField } from "../utils/workflow-run-utils"

type FileMode = "none" | "optional" | "required"

type WorkflowTestPanelProps = {
  workflowId: string | undefined
  isOpen: boolean
  onClose: () => void
  /** When true, shows the text input area. When false, text input is hidden. */
  allowTextInput?: boolean
  /** File upload mode: none (no files), optional (can upload), required (must upload) */
  fileMode?: FileMode
}

export function WorkflowTestPanel({
  workflowId,
  isOpen,
  onClose,
  allowTextInput = false,
  fileMode = "none",
}: WorkflowTestPanelProps) {
  const { t } = useTranslation()
  const [settingsService] = useState(() => SettingsService.getInstance())
  const [inputValue, setInputValue] = useState("")
  const [files, setFiles] = useState<File[]>([])
  // Per-text-field values keyed by each start field's key (e.g. text_0). Drives
  // the multi-field form so the test run sends user_input_data exactly like
  // production. Untouched fields stay absent until edited.
  // Keyed by field.key; entries are absent until the user types, so values are
  // optional (justifies the ?./?? guards on read).
  const [fieldTextValues, setFieldTextValues] = useState<Record<string, string | undefined>>({})
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartXRef = useRef<number | null>(null)
  const resizeStartWidthRef = useRef<number | null>(null)
  // Ref on the ScrollArea Root. The actual scrollable element is the Radix
  // Viewport ([data-radix-scroll-area-viewport]) nested inside it — the inner
  // content div is not scrollable, so auto-scroll must target the Viewport.
  const outputScrollAreaRef = useRef<HTMLDivElement>(null)
  const completedNodeIds = useWorkflowStore((state) => state.completedNodeIds)
  const executingNodeIds = useWorkflowStore((state) => state.executingNodeIds)
  const executionError = useWorkflowStore((state) => state.executionError)
  const trackNodeOutputs = useWorkflowStore((state) => state.trackNodeOutputs)
  const setTrackNodeOutputs = useWorkflowStore((state) => state.setTrackNodeOutputs)
  const testPanelWidth = useWorkflowStore((state) => state.testPanelWidth)
  const setTestPanelWidth = useWorkflowStore((state) => state.setTestPanelWidth)
  const nodes = useWorkflowStore((state) => state.nodes)

  // Resolve the start node's effective input fields (new fields[] or legacy-
  // derived). When fields[] is declared we render a dynamic per-field form and
  // build user_input_data exactly like the production run dialog; otherwise we
  // fall back to the legacy single free-form input driven by allowTextInput.
  const startFields = useMemo<StartField[]>(() => {
    const startNode = nodes.find((node) => node.type === "start")
    // node.data is React Flow's loosely-typed Record; cast to the resolver's
    // expected start-node shape (it reads only fields/requiresInput/fileMode).
    return resolveStartFields(startNode?.data as Parameters<typeof resolveStartFields>[0])
  }, [nodes])
  const textFields = useMemo(
    () => startFields.filter((field) => field.type === "text"),
    [startFields]
  )
  const hasFileField = useMemo(
    () => startFields.some((field) => field.type === "file"),
    [startFields]
  )
  // Use the dynamic multi-field form when the start node declares more than one
  // field OR any file field. The dynamic form is the only path that renders a file
  // picker (text fields ride per-field inputs, files ride the shared picker), so a
  // lone file field must use it too — otherwise the legacy path shows no picker
  // (allowFiles keys off legacy fileMode) and the file can't be supplied. A single
  // text field (or none) keeps the original legacy UI.
  const useFieldForm = startFields.length > 1 || hasFileField

  const { isRunning, output, error, activities, runWorkflow, stopWorkflow, clearResults } =
    useWorkflowTestRun({
      workflowId,
    })
  const latestActivityIndexByNode = useMemo(() => {
    const indexMap = new Map<string, number>()
    activities.forEach((activity, index) => {
      indexMap.set(activity.nodeId, index)
    })
    return indexMap
  }, [activities])

  // File picker state
  const allowFiles = fileMode !== "none"
  const [filePickerOpen, setFilePickerOpen] = useState(false)

  const handleLocalFilesSelected = useCallback((selectedFiles: File[]) => {
    setFiles((prev) => [...prev, ...selectedFiles])
  }, [])

  const handleGraphFileSelected = useCallback(
    async (item: Parameters<typeof downloadGraphFileApi>[0]) => {
      const blob = await downloadGraphFileApi(item, settingsService.token)
      const mime = item.mimeType || blob.type || "application/octet-stream"
      const file = new File([blob], item.name, { type: mime })
      setFiles((prev) => [...prev, file])
    },
    [settingsService]
  )

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // Auto-scroll output to bottom. The Radix ScrollArea scrolls on its Viewport,
  // not the Root or the inner content div — so reach into the rendered Viewport
  // ([data-radix-scroll-area-viewport]) and drive its scrollTop. Setting it on
  // the inner content div (as a previous version did) is a no-op.
  useEffect(() => {
    const viewport = outputScrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]"
    )
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight
    }
  }, [output])

  // user_input_data: START text fields (files ride the upload pipeline) plus the
  // answers for mid-flow User Input nodes, which Test Run cannot pause at (#2964).
  const {
    userInputNodes,
    userInputAnswers,
    setUserInputAnswer,
    clearUserInputAnswers,
    buildUserInputData,
  } = useTestRunUserInputs({ nodes, useFieldForm, textFields, fieldTextValues, inputValue })

  const handleRun = useCallback(() => {
    // The first text field's value still flows as the chat message ({input}) for
    // back-compat; user_input_data carries every text field for {field.key} use.
    const userInputData = buildUserInputData()
    // First text field's value still flows as the chat message ({input}); avoid a
    // nested ternary for the dynamic-vs-legacy + has-field branches.
    let firstTextValue = inputValue
    if (useFieldForm) {
      firstTextValue =
        textFields.length > 0 ? (fieldTextValues[textFields[0].key]?.trim() ?? "") : ""
    }
    const hasUserInputData = Object.keys(userInputData).length > 0
    void runWorkflow(
      firstTextValue || undefined,
      files.length > 0 ? files : undefined,
      hasUserInputData ? userInputData : undefined
    )
  }, [
    buildUserInputData,
    useFieldForm,
    textFields,
    fieldTextValues,
    inputValue,
    runWorkflow,
    files,
  ])

  const handleStop = useCallback(() => {
    stopWorkflow()
  }, [stopWorkflow])

  const handleClear = useCallback(() => {
    clearResults()
    setInputValue("")
    setFiles([])
    setFieldTextValues({})
    clearUserInputAnswers()
  }, [clearResults, clearUserInputAnswers])

  const handleFieldTextChange = useCallback((key: string, value: string) => {
    setFieldTextValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !isRunning) {
        e.preventDefault()
        handleRun()
      }
    },
    [handleRun, isRunning]
  )

  // Resize handlers
  const handleStartResize = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      resizeStartXRef.current = event.clientX
      resizeStartWidthRef.current = testPanelWidth
      setIsResizing(true)
    },
    [testPanelWidth]
  )

  useEffect(() => {
    if (!isResizing) return

    const handlePointerMove = (event: PointerEvent) => {
      if (resizeStartXRef.current == null || resizeStartWidthRef.current == null) return
      const delta = resizeStartXRef.current - event.clientX
      setTestPanelWidth(resizeStartWidthRef.current + delta)
    }

    const handlePointerUp = () => {
      resizeStartXRef.current = null
      resizeStartWidthRef.current = null
      setIsResizing(false)
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)
    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
    }
  }, [isResizing, setTestPanelWidth])

  // Check if Run button should be disabled. In the dynamic form, gate on required
  // TEXT fields AND required FILE fields (the dynamic form's shared picker can
  // supply files, so a required file must be provided — matching the production run
  // dialog, which blocks the same input). The legacy single-input path keeps its
  // original fileMode === "required" gate.
  const requiredTextFieldsUnfilled = textFields.some(
    (field) => field.required && (fieldTextValues[field.key]?.trim() ?? "").length === 0
  )
  // Files ride the shared picker (flat transport), so any required file field is
  // satisfied once at least one file is selected.
  const requiredFileFieldUnfilled =
    startFields.some((field) => field.type === "file" && field.required) && files.length === 0
  // A start node with exactly ONE text field uses the legacy single-input path
  // (useFieldForm is false), where that field's value flows through the free-form
  // textarea (inputValue). It must still be validated when required — otherwise a
  // lone required field could be submitted empty (only fileMode was gated before).
  const loneRequiredTextUnfilled =
    !useFieldForm &&
    textFields.length === 1 &&
    textFields[0].required === true &&
    inputValue.trim().length === 0
  const isRunDisabled = useFieldForm
    ? requiredTextFieldsUnfilled || requiredFileFieldUnfilled
    : (fileMode === "required" && files.length === 0) || loneRequiredTextUnfilled

  // Whether any input UI is rendered above the Run button (controls top spacing).
  const hasInputUi =
    (useFieldForm ? startFields.length > 0 : allowTextInput || allowFiles) ||
    userInputNodes.length > 0
  // The ⌘+Enter hint only makes sense when at least one text input is rendered.
  const showShortcutHint = useFieldForm ? textFields.length > 0 : allowTextInput

  if (!isOpen) return null

  const hasOutput = output.length > 0
  const hasActivities = activities.length > 0
  const hasError = !!error || !!executionError
  const isComplete = !isRunning && (hasOutput || hasError)

  return (
    <>
      <div
        style={{ width: `${testPanelWidth}px` }}
        className={cn(
          "bg-background relative flex h-full shrink-0 flex-col border-l",
          // Float over the canvas as a drawer on small screens.
          "max-lg:absolute max-lg:inset-y-0 max-lg:right-0 max-lg:z-30 max-lg:!w-full max-lg:!max-w-sm max-lg:shadow-2xl"
        )}
      >
        {/* Resize handle */}
        <div
          aria-label="Resize test panel"
          role="separator"
          className={cn(
            "absolute top-0 left-0 z-10 flex h-full w-3 cursor-col-resize items-center justify-center",
            isResizing ? "bg-muted" : "hover:bg-muted/50 bg-transparent"
          )}
          onPointerDown={handleStartResize}
        >
          <GripVertical className="text-muted-foreground size-4" />
        </div>

        {/* Header */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <TestTube2 className="text-warning-ink size-4" />
            <span className="font-medium">{t("workflowLanding.testPanel.title", "Test Run")}</span>
            {isRunning && (
              <Badge className="bg-warning/30 text-warning-ink h-5 animate-pulse" variant="outline">
                {t("workflowLanding.testPanel.running", "Running")}
              </Badge>
            )}
            {isComplete && !hasError && (
              <Badge
                className="bg-success/15 border-success text-foreground h-5 border"
                variant="outline"
              >
                <CheckCircle2 className="mr-1 size-3" />
                {t("workflowLanding.testPanel.complete", "Complete")}
              </Badge>
            )}
            {hasError && (
              <Badge
                className="bg-destructive/15 border-destructive text-foreground h-5 border"
                variant="outline"
              >
                <AlertCircle className="mr-1 size-3" />
                {t("workflowLanding.testPanel.error", "Error")}
              </Badge>
            )}
          </div>
          <Button className="size-7" size="icon" variant="ghost" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {/* Input & Controls Section */}
          <div className="shrink-0 border-b p-4">
            <WorkflowTestPanelInputs
              allowFiles={allowFiles}
              allowTextInput={allowTextInput}
              fieldTextValues={fieldTextValues}
              fileMode={fileMode}
              files={files}
              handleFieldTextChange={handleFieldTextChange}
              handleKeyDown={handleKeyDown}
              handleUserInputAnswerChange={setUserInputAnswer}
              hasFileField={hasFileField}
              inputValue={inputValue}
              isRunning={isRunning}
              removeFile={removeFile}
              setFilePickerOpen={setFilePickerOpen}
              setInputValue={setInputValue}
              textFields={textFields}
              useFieldForm={useFieldForm}
              userInputAnswers={userInputAnswers}
              userInputNodes={userInputNodes}
            />

            <div className={cn("flex items-center gap-2", hasInputUi && "mt-3")}>
              {isRunning ? (
                <Button className="flex-1" variant="destructive" onClick={handleStop}>
                  <Square className="mr-2 size-3" />
                  {t("workflowLanding.testPanel.stop", "Stop")}
                </Button>
              ) : (
                <Button className="flex-1" disabled={isRunDisabled} onClick={handleRun}>
                  <Play className="mr-2 size-3" />
                  {t("workflowLanding.testPanel.run", "Run Test")}
                </Button>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("workflowLanding.testPanel.clearResults", "Clear results")}
                    disabled={isRunning || (!hasOutput && !hasActivities)}
                    size="icon"
                    variant="outline"
                    onClick={handleClear}
                  >
                    <RotateCcw className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("workflowLanding.testPanel.clearResults", "Clear results")}
                </TooltipContent>
              </Tooltip>
            </div>
            {showShortcutHint && (
              <p className="text-muted-foreground mt-2 text-center text-[10px]">
                {t("workflowLanding.testPanel.shortcut", "⌘+Enter to run")}
              </p>
            )}

            <div className="mt-3 flex items-center justify-between border-t pt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Eye className="text-muted-foreground size-3.5" />
                    <span className="text-muted-foreground text-xs">
                      {t("workflowLanding.testPanel.trackOutputs", "Track node outputs")}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[200px]" side="bottom">
                  <p className="text-xs">
                    {t(
                      "workflowLanding.testPanel.trackOutputsTooltip",
                      "When enabled, click on completed nodes to view their output"
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
              <Switch
                checked={trackNodeOutputs}
                disabled={isRunning}
                onCheckedChange={setTrackNodeOutputs}
              />
            </div>
          </div>

          {/* Activity Timeline */}
          {hasActivities && (
            <div className="shrink-0 border-b">
              <div className="bg-muted/30 flex items-center gap-2 border-b px-4 py-2">
                <span className="text-muted-foreground text-xs font-medium">
                  {t("workflowLanding.testPanel.activity", "Activity")}
                </span>
                <Badge className="bg-muted text-foreground h-4 text-[10px]" variant="outline">
                  {activities.length}
                </Badge>
              </div>
              <ScrollArea className="h-[120px]">
                <div className="space-y-1 p-2">
                  {activities.map((activity, index) => {
                    const isLatestForNode = latestActivityIndexByNode.get(activity.nodeId) === index
                    const isActive = isLatestForNode && executingNodeIds.includes(activity.nodeId)
                    const isNodeCompleted = completedNodeIds.includes(activity.nodeId)
                    const getActivityIcon = () => {
                      if (isActive) {
                        return (
                          <Loader2 className="text-success mt-0.5 size-3 shrink-0 animate-spin" />
                        )
                      }
                      if (isNodeCompleted) {
                        return <CheckCircle2 className="text-success mt-0.5 size-3 shrink-0" />
                      }
                      return (
                        <ChevronRight className="text-muted-foreground mt-0.5 size-3 shrink-0" />
                      )
                    }
                    return (
                      <div
                        key={`${activity.nodeId}-${activity.timestamp}-${index}`}
                        className={cn(
                          "flex items-start gap-2 rounded-md px-2 py-1.5 text-xs",
                          isActive && "bg-success/10",
                          isNodeCompleted && !isActive && "text-muted-foreground"
                        )}
                      >
                        {getActivityIcon()}
                        <span className="line-clamp-2">{activity.message}</span>
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Output Section */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="bg-muted/30 flex items-center gap-2 border-b px-4 py-2">
              <span className="text-muted-foreground text-xs font-medium">
                {t("workflowLanding.testPanel.output", "Output")}
              </span>
              {isRunning && <Loader2 className="text-muted-foreground size-3 animate-spin" />}
            </div>
            <ScrollArea ref={outputScrollAreaRef} className="flex-1">
              <div className="p-4">
                {hasError && (
                  <div className="border-destructive bg-destructive/15 mb-4 rounded-md border p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                      <div className="text-foreground text-sm">{error || executionError}</div>
                    </div>
                  </div>
                )}
                {hasOutput && (
                  <div className="max-w-full min-w-0">
                    <MarkdownRenderer
                      content={output}
                      className={cn(
                        "prose-sm max-w-full [overflow-wrap:anywhere] [&_*]:[overflow-wrap:anywhere] [&>*]:max-w-full",
                        // Compact headings: the default prose h1/h2 sizes overflow this
                        // narrow panel, so scale them down with tighter margins.
                        "[&_h1]:mt-3 [&_h1]:mb-1.5 [&_h1]:text-base [&_h1]:font-semibold",
                        "[&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-sm [&_h2]:font-semibold",
                        "[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-medium",
                        "[&_h4]:mt-2 [&_h4]:mb-1 [&_h4]:text-xs [&_h4]:font-medium",
                        "[&_h1]:first:mt-0 [&_h2]:first:mt-0 [&_h3]:first:mt-0 [&_h4]:first:mt-0"
                      )}
                    />
                  </div>
                )}
                {!hasOutput && !hasError && !isRunning && (
                  <div className="text-muted-foreground flex h-32 items-center justify-center text-center text-sm">
                    {t("workflowLanding.testPanel.noOutput", "Run the workflow to see output here")}
                  </div>
                )}
                {isRunning && !hasOutput && (
                  <div className="flex h-32 items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="text-muted-foreground size-6 animate-spin" />
                      <span className="text-muted-foreground text-sm">
                        {t("workflowLanding.testPanel.executing", "Executing workflow...")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      <FilePickerDialog
        open={filePickerOpen}
        source="local"
        onLocalFilesSelected={handleLocalFilesSelected}
        onOpenChange={setFilePickerOpen}
        onSelect={handleGraphFileSelected}
      />
    </>
  )
}
